import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { REACT_APP_API_TENANTS_URL, ORG_ID, DOMAIN_ID } from "../../constants";
import {
  Button,
  Card,
  Form,
  Typography,
  Tooltip,
  Dropdown,
  Popover,
  message,
  Modal,
  Image,
  Select,
  Input,
} from "antd";
import {
  AppstoreOutlined,
  AppstoreTwoTone,
  AppstoreAddOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { ContainedButton } from "../button/button";
import { fetchAppList, updateApp } from "./appsApi";
import { Spinner } from "../../utils/helpers";

const { Title, Text } = Typography;

function Apps() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [sortType, setSortType] = useState();
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [visiblePopoverId, setVisiblePopoverId] = useState(null);
  const [form] = Form.useForm();
  const [msgApi, contextHolder] = message.useMessage();
  const options = [
    { label: "Name (A → Z)", value: "name-asc" },
    { label: "Name (Z → A)", value: "name-desc" },
    { label: "Last Added", value: "last-added" },
  ];

  const filteredApps = useMemo(() => {
    if (!searchText.trim()) return apps;

    return apps.filter((app) =>
      app.appName?.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [apps, searchText]);

  const sortedApps = useMemo(() => {
    const data = [...filteredApps]; // copy is important

    switch (sortType) {
      case "name-asc":
        return data.sort((a, b) => a.appName.localeCompare(b.appName));

      case "name-desc":
        return data.sort((a, b) => b.appName.localeCompare(a.appName));

      case "last-added":
      default:
        return data.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
    }
  }, [filteredApps, sortType]);

  const handleOpen = (app) => {
    if (app.authMethod === "SAML2" && app?.appUrl) {
      window.open(app.appUrl, "_blank");
    } else if (app.authMethod === "OIDC" && app?.authUrl) {
      window.open(app.authUrl, "_blank");
    } else {
      console.warn("No URL found for the selected app");
    }
  };
  const handleSuccess = (data) => {
    const fetchedApps = data?.apps ?? data;
    setApps((prev) => {
      // dedupe by id, keeping latest from fetchedApps if conflict
      const map = new Map();
      [...prev, ...fetchedApps].forEach((app) => {
        map.set(app.appId, app);
      });
      return Array.from(map.values());
    });
    setLoading(false);
  };

  const handleFailure = ({ message }) => {
    console.log("message", message);
    setLoading(false);
  };

  // Fetch Existing apps
  useEffect(() => {
    setLoading(true);
    let payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
    };
    fetchAppList({
      payload,
      onSuccess: handleSuccess,
      onFailure: handleFailure,
    });
  }, []);

  const getMenuItems = (app) => [
    {
      key: "open",
      label: "Open",
      onClick: () => handleOpen(app),
    },
    {
      key: "edit",
      label: "Edit",
      onClick: () => handleEditOrDelete(app, "edit"),
    },
    {
      key: "delete",
      label: "Delete",
      onClick: () => handleEditOrDelete(app, "delete"),
      danger: true,
    },
    {
      key: "description",
      label: "View Description",
      onClick: (e) => {
        e.domEvent.stopPropagation();
        setVisiblePopoverId(app.appId);
      },
    },
  ];

  // Function for Edit/delete
  const handleEditOrDelete = (app, action) => {
    if (action === "edit") {
      // navigate to add/edit page with existing app as state for prefill
      navigate("/pam/apps/add", { state: { app, isEdit: true } });
    } else if (action === "delete") {
      Modal.confirm({
        title: "Delete Application",
        content: `Are you sure you want to delete "${app.appName || app.name}"?`,
        okText: "Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          try {
            // Adjust endpoint/payload if delete is soft or requires specific params
            const formData = new FormData();
            formData.append("orgId", String(ORG_ID()));
            formData.append("tenantId", String(DOMAIN_ID()));
            formData.append("appId", app.appId);
            formData.append("userId", app.userId);
            formData.append("authMethod", app.authMethod);
            formData.append("status", "INACTIVE");
            updateApp({
              payload: formData,
              onSuccess: handleDeletOrUpdateSuccess(app),
              onFailure: handleDeleteOrUpdateFailure,
            });
            // Could be a deleteApps endpoint or updateApps with status=deleted

            // refresh list logic here (e.g., refetch or state update)
          } catch (err) {
            console.error("Delete failed:", err?.response?.status, err?.message);
            msgApi.error(
              err?.response?.data?.message || "Failed to delete application.",
            );
          }
        },
      });
    }
  };

  const handleDeletOrUpdateSuccess = ({ app }) => {
    msgApi.success("Application deleted.");
    //Update app list in UI after successful delete
    setApps((prev) => prev.filter((a) => a.appId !== app.appId));
  };

  const handleDeleteOrUpdateFailure = () => {};
  const onSearch = (value, _e, info) => console.log(info?.source, value);
  return (
    <>
      {contextHolder}
      <div className=" flex flex-col p-6 relative border border-spacing-2 !bg-slate-50 !h-full overflow-auto">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6 py-3">
          <div className="flex items-center space-x-3">
            <div className="bg-white rounded-sm shadow p-3 flex justify-center items-center">
              <AppstoreOutlined className="!text-2xl" />
            </div>
            <div>
              <Title level={4} className="!mb-0">
                {" "}
                My Applications{" "}
              </Title>
              <Text type="secondary" className="!text-sm !text-slate-400">
                Manage and access your connected services
              </Text>
            </div>
          </div>
          <div className="w-full md:w-auto">
            <ContainedButton
              customClass=" w-full md:w-auto px-3 py-4 !text-base tracking-tight"
              Icon={AppstoreAddOutlined}   
              buttonName={"Add Application"}
              handleOnClick={() => navigate("/pam/apps/add")}
            />
          </div>
        </div>
        <div className="flex flex-col justify-center">
          {loading ? (
            <Spinner />
          ) : apps?.length === 0 ? (
            <div className="bg-slate-50  border border-dashed border-slate-200 rounded-3xl p-8 flex flex-col items-center justify-center text-center space-y-4 mb-8">
              <div className="w-16 h-16 bg-white  rounded-2xl shadow-sm flex items-center justify-center mb-2">
                <span className="material-symbols-rounded  text-3xl">
                  <AppstoreTwoTone />
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  No applications added yet
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Click <b>'Add Application'</b> to get started.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-between">
                <Input
                  placeholder="search your app"
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 320 }}
                  allowClear
                  onSearch={onSearch}
                  enterButton="Search"
                  className="rounded-lg border-slate-400"
                />
                <Select
                  value={sortType}
                  allowClear
                  placeholder="Sort"
                  classNames={{ root: "custom-select" }}
                  className="custom-select !w-32 text-lg"
                  onChange={setSortType}
                  options={options}
                />
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,180px))] gap-5 pt-10 pb-4 ">
                {sortedApps.map((app, index) => (
                  <Tooltip placement="bottom" key={index}>
                    <Card
                      hoverable
                      className=" relative text-center rounded-sm border-2 border-slate-200 transition-transform"
                      classNames={{
                        body: "!px-5 !py-6 !flex-col justify-center items-between",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "scale(1.05)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 16px rgba(0,0,0,0.12)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.05)";
                      }}
                    >
                      {/* Three dots menu */}
                      <Image
                        className="!max-w-14 !h-14 !max-h-14 !rounded"
                        onDoubleClick={() => handleOpen(app)}
                        preview={false}
                        src={app.appLogo || `${app.appName} logo`}
                        alt="icon"
                      />
                      {/* Dropdown */}
                      <div className="flex items-center justify-between gap-2 mt-5">
                        <Tooltip
                          title={app.appName}
                          placement="bottom"
                          classNames={{ root: "custom-tooltip" }}
                        >
                          <div className="font-semibold text-sm text-center truncate flex-1 capitalize">
                            {app.appName}
                          </div>
                        </Tooltip>

                        <div className="flex items-center gap-1">
                          <Dropdown
                            menu={{
                              items: getMenuItems(app),
                            }}
                            trigger={["click"]}
                            placement="bottomRight"
                            classNames={{
                              root: "custom-dropdown-menu",
                            }}
                          >
                            <Button
                              type="text"
                              shape="circle"
                              className="absolute top-1 right-1 text-lg"
                            >
                              ⋮
                            </Button>
                          </Dropdown>
                          {visiblePopoverId === app.appId && (
                            <Popover
                              placement="right"
                              classNames={{
                                root: "custom-description-popover",
                              }}
                              content={
                                <div className="max-w-[250px]">
                                  <div className="flex gap-2 justify-between items-center mb-2">
                                    <strong>About Application</strong>
                                    <span
                                      className="cursor-pointer text-red-500 text-xl leading-none transition-transform duration-200 hover:scale-150"
                                      onClick={(e) => {
                                        e.stopPropagation(); // Stops the click event from bubbling up to prevent unintended parent handlers.
                                        setVisiblePopoverId(null);
                                      }}
                                    >
                                      ×
                                    </span>
                                  </div>
                                  <p>
                                    {app.description.lenght > 1 ? (
                                      app.description
                                    ) : (
                                      <span>
                                        No description added yet. Click{" "}
                                        <strong>Edit</strong> to add details
                                        about this application.
                                      </span>
                                    )}
                                  </p>
                                </div>
                              }
                              open
                              trigger="click"
                            >
                              {/* Add a dummy anchor or something the popover can anchor to */}
                              <span></span>
                            </Popover>
                          )}
                        </div>
                      </div>
                    </Card>
                  </Tooltip>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default Apps;
